import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { AppDatePipe } from './pipes/app-date.pipe';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { providePrimeNG } from 'primeng/config';
import { Observable } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeFi from '@angular/common/locales/fi';
import localeSv from '@angular/common/locales/sv';
import localePt from '@angular/common/locales/pt';
import localeEs from '@angular/common/locales/es';
import localePl from '@angular/common/locales/pl';
import localeEt from '@angular/common/locales/et';
import localeLt from '@angular/common/locales/lt';
import localeLv from '@angular/common/locales/lv';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

import { routes } from './app.routes';
import { AuthService } from './services/auth.service';
import { LocaleProvider } from './services/locale.provider';
import { AdditionalInfoInterceptor } from './interceptors/additional-info.interceptor';
import { UnathorizedInterceptor } from './interceptors/UnathorizedInterceptor';
import { ErrorLoggingInterceptor } from './interceptors/error-logging.interceptor';
import { StorageMutationInterceptor } from './interceptors/storage-mutation.interceptor';
import { LabChangeInterceptor } from './interceptors/lab-change.interceptor';
import { PersonRouteReuseStrategy } from './guards/person-route-reuse.strategy';
import { GlobalErrorHandler } from './handlers/global-error-handler';
import { importProvidersFrom } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { UserContextService } from './services/user-context.service';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f3f2f6',
      100: '#c3c2d4',
      200: '#9491b2',
      300: '#656191',
      400: '#36306f',
      500: '#07004d',
      600: '#060041',
      700: '#050036',
      800: '#04002a',
      900: '#03001f',
      950: '#020013'
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#fffdfc',
          100: '#fff6f1',
          200: 'rgba(255, 217, 197, 0.5)',
          300: '#ffe7db',
          400: '#07004D',
          500: '#07004D',
          600: '#d9b8a7',
          700: '#07004D',
          800: '#8c776c',
          900: '#66574f',
          950: '#403631'
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: '{surface.200}'
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.400}',
          color: '{surface.700}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '{red.600}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)'
        },
        text: {
          color: '{surface.700}',
          hoverColor: '{surface.800}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}'
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}'
          }
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}',
              activeColor: '{surface.500}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}'
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.500}',
            activeColor: '{surface.500}'
          }
        }
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f3f2f6',
          100: '#c3c2d4',
          200: '#9491b2',
          300: '#656191',
          400: '#36306f',
          500: '#07004d',
          600: '#060041',
          700: '#050036',
          800: '#04002a',
          900: '#03001f',
          950: '#020013'
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}'
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        },
        mask: {
          background: 'rgba(0,0,0,0.6)',
          color: '{surface.200}'
        },
        formField: {
          background: '{surface.950}',
          disabledBackground: '{surface.700}',
          filledBackground: '{surface.800}',
          filledHoverBackground: '{surface.800}',
          filledFocusBackground: '{surface.800}',
          borderColor: '{surface.600}',
          hoverBorderColor: '{surface.500}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.300}',
          color: '{surface.0}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{red.400}',
          floatLabelColor: '{surface.400}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.400}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)'
        },
        text: {
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}'
        },
        content: {
          background: '{surface.900}',
          hoverBackground: '{surface.800}',
          borderColor: '{surface.700}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{surface.800}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.400}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}'
          }
        },
        navigation: {
          item: {
            focusBackground: '{surface.800}',
            activeBackground: '{surface.800}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.400}',
              activeColor: '{surface.400}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}'
          },
          submenuIcon: {
            color: '{surface.500}',
            focusColor: '{surface.400}',
            activeColor: '{surface.400}'
          }
        }
      }
    }
  }
});

// Register locales
registerLocaleData(localeFi);
registerLocaleData(localePt);
registerLocaleData(localeSv);
registerLocaleData(localeEs);
registerLocaleData(localePl);
registerLocaleData(localeEt);
registerLocaleData(localeLt);
registerLocaleData(localeLv);

// JWT factory to automatically attach Bearer token to each api call
export function jwtOptionsFactory(authService: AuthService) {
  return {
    tokenGetter: () => {
      return authService.getToken();
    },
    disallowedRoutes: [
      'http://localhost:3000/alineApi/login',
      'http://localhost:3000/alineApi/logout',
      'http://betaapi.alinesoft.com/login',
      'http://betaapi.alinesoft.com/logout',
      'https://betaapi.alinesoft.com/login',
      'https://betaapi.alinesoft.com/logout',
      'http://testapi.alinesoft.com/login',
      'http://testapi.alinesoft.com/logout',
      'https://testapi.alinesoft.com/login',
      'https://testapi.alinesoft.com/logout',
      'https://api.alinesoft.com/login',
      'https://api.alinesoft.com/logout'
    ],
    allowedDomains: [
      'localhost:3000',
      'betaapi.alinesoft.com',
      'testapi.alinesoft.com',
      'api.alinesoft.com'
    ]
  };
}

export class CustomLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  public getTranslation(lang: string): Observable<any> {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/i18n/${lang}.json`;
    return this.http.get<any>(url);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    { provide: RouteReuseStrategy, useClass: PersonRouteReuseStrategy },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    importProvidersFrom(
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [AuthService]
        }
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: CustomLoader,
          deps: [HttpClient]
        },
        fallbackLang: 'en'
      })
    ),
    LocaleProvider,
    DatePipe,
    AppDatePipe,
    MessageService,
    ConfirmationService,
    DialogService,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorLoggingInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AdditionalInfoInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnathorizedInterceptor,
      multi: true
    },
    {
      // Invalidates every straw counter after a storage-mutating request
      // (freeze/thaw/discard/SEC/reservation/...). Injects only the
      // dependency-free StorageChangeBusService — no DI cycle.
      provide: HTTP_INTERCEPTORS,
      useClass: StorageMutationInterceptor,
      multi: true
    },
    {
      // Sends the user action of a lab save for the lab change log.
      provide: HTTP_INTERCEPTORS,
      useClass: LabChangeInterceptor,
      multi: true
    },
    {
      // Pull privileges/groups out of /me/context before the app boots
      // so route guards and components can read them synchronously.
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService, UserContextService],
      useFactory: (auth: AuthService, ctx: UserContextService) => () => {
        if (!auth.isAuthenticated()) {
          return Promise.resolve();
        }
        return ctx.loadContextAsync().catch(() => undefined);
      }
    }
  ]
};
